import { useMemo, ChangeEvent, Fragment } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import merge from "lodash/merge";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { LANGUAGE_SELECT_OPTIONS } from "@constants/language-select-options";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { resources } from "@constants/hardcoded-strings";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { convertDotSeparatedStringToJSONObject } from "@utils/string-util";
import { getMessagingTemplateEmailSubjectData, getMessagingTemplateVariables } from "@utils/messaging-template-util";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { AddMessagingTemplateLanguageModal } from "@containers/modals/add-messaging-template-language-modal/add-messaging-template-language-modal";
import commonStyles from "@styles/common.less";
import { MessagingTemplateVariablesValidationErrors } from "@idw-types/common/validation/messaging-template/messaging-template-validation-errors";

type MessagingTemplateVariablesFormSectionProps = {
    state: FormattedMessagingTemplate;
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    selectedLanguage: LocaleTypes;
    validationErrors: MessagingTemplateVariablesValidationErrors;
    setSelectedLanguage: (language: LocaleTypes) => void;
    setMessagingTemplateState: (state: FormattedMessagingTemplate) => void;
};

export const MessagingTemplateVariablesFormSection = (props: MessagingTemplateVariablesFormSectionProps) => {
    const {
        state,
        labelWidth,
        isDisabled,
        selectedLanguage,
        validationErrors,
        setSelectedLanguage,
        setMessagingTemplateState
    } = props;

    const resource = resources.pages.messagingTemplateDetails.tabs.variables;

    const modalContext = useModalContext();

    const {
        selectedLanguages,
        availableLanguages
    } = useMemo(() => {
        const selectedLanguages = Object.keys(state.languageFiles) as Array<LocaleTypes>;
        const availableLanguages = LANGUAGE_SELECT_OPTIONS.filter(lso => !selectedLanguages.includes(lso.value));

        return {
            selectedLanguages,
            availableLanguages
        };
    }, [state.languageFiles]);

    const variables = useMemo(() => {
        return getMessagingTemplateVariables(state.languageFiles, state.template, selectedLanguage);
    }, [state.languageFiles, state.template, selectedLanguage]);

    const {
        shouldHaveSubjectVariable,
        subjectVariableValue,
        subjectVariableName
    } = useMemo(() => {
        return getMessagingTemplateEmailSubjectData(state.type, state.languageFiles[selectedLanguage]);
    }, [state.languageFiles, selectedLanguage, state.type]);


    const handleUpdateLanguage = (newOption: SelectOption<LocaleTypes>) => {
        setSelectedLanguage(newOption.value);
    };

    const handleUpdateEmailSubject = (event: ChangeEvent<HTMLInputElement>) => {
        let newJSONObject = {};
        if (state.type === MessagingTemplateTypes.DCUI_EMAIL) {
            newJSONObject = { dcuiEmail: { subject: event.target.value } };
        } else if (state.type === MessagingTemplateTypes.PIN_EMAIL) {
            newJSONObject = { emailpin: { subject: event.target.value } };
        }

        const newLanguageFile = merge({}, state.languageFiles[selectedLanguage], newJSONObject);

        setMessagingTemplateState({
            ...state,
            languageFiles: {
                ...state.languageFiles,
                [selectedLanguage]: newLanguageFile
            }
        });
    };

    const handleChangeAtIndex = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const newJSONObject = convertDotSeparatedStringToJSONObject(variables[index].name, event.target.value);
        const newLanguageFile = merge({}, state.languageFiles[selectedLanguage], newJSONObject);

        setMessagingTemplateState({
            ...state,
            languageFiles: {
                ...state.languageFiles,
                [selectedLanguage]: newLanguageFile
            }
        });
    };


    const onClickContinue = (localeType: LocaleTypes) => {
        setMessagingTemplateState({
            ...state,
            languageFiles: {
                ...state.languageFiles,
                [localeType]: state.languageFiles[LocaleTypes.ENGLISH]
            }
        });
        setSelectedLanguage(localeType);
    };

    const onClickAddLanguage = () => {
        if (setSelectedLanguage) {
            const renderAddMessagingTemplateLanguageModal = (onModalClose: () => void) => {
                return (
                    <AddMessagingTemplateLanguageModal
                        availableLanguages={availableLanguages}
                        onClickContinue={onClickContinue}
                        onModalClose={onModalClose}
                    />
                );
            };

            modalContext.setModalState({
                modalType: ModalTypes.AddMessagingTemplateLanguage,
                renderFunction: renderAddMessagingTemplateLanguageModal
            });
        } else {
            setMessagingTemplateState({
                ...state,
                languageFiles: {
                    ...state.languageFiles,
                    [selectedLanguage]: state.languageFiles[LocaleTypes.ENGLISH]
                }
            });
        }
    };

    const languageOptions = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.filter(lso => selectedLanguages.includes(lso.value));
    }, [state.languageFiles, selectedLanguages]);

    const selectedLanguageOption = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.find(lso => lso.value === selectedLanguage) || null;
    }, [state.languageFiles, selectedLanguage]);

    const {
        languageValidationErrors
    }: { languageValidationErrors: { [id: string]: React.ReactNode }; count: number; } = useMemo(() => {
        let errors = {};
        let count = 0;
        if (validationErrors?.multiLanguageValidationErrors[selectedLanguage]) {
            const data = validationErrors.multiLanguageValidationErrors[selectedLanguage];
            errors = data.errors || {};
            count = data.count;
        }
        return {
            languageValidationErrors: errors,
            count
        };
    }, [validationErrors, selectedLanguage]);

    const {
        emailSubjectValidationErrors
    }: { emailSubjectValidationErrors: { [id: string]: React.ReactNode }; count: number; } = useMemo(() => {
        let errors = {};
        let count = 0;

        if (validationErrors) {
            if (validationErrors.multiLanguageEmailSubjectErrors[selectedLanguage]) {
                const data = validationErrors.multiLanguageEmailSubjectErrors[selectedLanguage];
                errors = data.errors || {};
                count = data.count;
            }
        }

        return {
            emailSubjectValidationErrors: errors,
            count
        };
    }, [validationErrors, selectedLanguage]);

    return (
        <ThemingFormSection title={resource.sections.variables.title}>
            <div className={commonStyles.sectionDescription}>{resource.sections.variables.description}</div>
            {setSelectedLanguage && (
                <div>
                    <SelectFormGroup
                        controlId={`messaging-template-language-select`}
                        automationId={`messaging-template-language-select`}
                        ariaLabel={resource.inputs.language.label}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        selectedOption={selectedLanguageOption}
                        options={languageOptions}
                        onChange={handleUpdateLanguage}
                    />
                    {!isDisabled && availableLanguages.length > 0 && (
                        <Button onClick={onClickAddLanguage}>
                            {resource.buttons.addLanguage.label}
                        </Button>
                    )}
                    <hr />
                </div>
            )}
            {!setSelectedLanguage && (
                <div>
                    {!selectedLanguages.includes(selectedLanguage) && (
                        <Alert variant="warning">{resource.noLanguageMessage(selectedLanguageOption?.label, isDisabled)}</Alert>
                    )}
                    {!isDisabled && !selectedLanguages.includes(selectedLanguage) && (
                        <Button onClick={onClickAddLanguage}>
                            {resource.buttons.addLanguage.label}
                        </Button>
                    )}
                    <hr />
                </div>
            )}
            {selectedLanguages.includes(selectedLanguage) && (
                <div>
                    <>
                        {shouldHaveSubjectVariable && (
                            <>
                                <TextareaFormGroup
                                    controlId={`messaging-template-subject-variable`}
                                    automationId={`messaging-template-subject-variable`}
                                    label={subjectVariableName}
                                    ariaLabel={subjectVariableName}
                                    isRequired={true}
                                    onChange={handleUpdateEmailSubject}
                                    value={subjectVariableValue}
                                    disabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={LayoutTypes.COLUMN}
                                />
                                {emailSubjectValidationErrors["subject"] || null}
                            </>
                        )}
                        {variables.map((variable, index) => (
                            <Fragment key={`messaging-template-variable-${variable.name}`}>
                                <TextareaFormGroup
                                    controlId={`messaging-template-variable-${variable.name}`}
                                    automationId={`messaging-template-variable-${variable.name}`}
                                    label={variable.name}
                                    ariaLabel={variable.name}
                                    isRequired={true}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChangeAtIndex(event, index)}
                                    value={variable.value}
                                    disabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={LayoutTypes.COLUMN}
                                />
                                {languageValidationErrors[variable.name] || null}
                            </Fragment>
                        ))}
                    </>
                    {variables.length === 0 && (
                        <Alert>{resource.noVariablesMessage}</Alert>
                    )}
                </div>
            )}
        </ThemingFormSection>
    );
};